/**
 * Builds the purchase order as A4 pages of real HTML (off screen), in the
 * "Order Request" design. Each block is measured first, then packed onto
 * pages so a long order breaks cleanly: page 1 carries the header band and
 * details, later pages a slim strip and the table head again, and every
 * page the footer. The generator turns each page into a PDF page.
 */
import type { Purchase, PurchaseItem } from "../../services/procurementService";
import type { ColumnConfig, CompanyInfo, HeaderFieldConfig } from "../pdfGenerator";
import { BrandGroup, PackBlock, PoLine, formatMoney, groupByBrand, packPages } from "./layout";
import { PO_FONTS as F, PO_THEME as T } from "./theme";

export const PAGE_W = 794; // A4 at 96 px per inch
export const PAGE_H = 1123;
const FOOTER_BOTTOM = 28;
const FOOTER_GAP = 18;
const BODY_TOP = 26;

export interface PoPageOptions {
    columns: ColumnConfig[];
    headerFields: HeaderFieldConfig[];
    includeShipping: boolean;
    showSummary: boolean;
    companyInfo: CompanyInfo;
    logoDataUrl: string | null;
    generatedAt: Date;
    /** product_id → photo data URL, for the Photo column (loadThumbnails). */
    thumbnails?: Map<string, string>;
}

export interface BuiltPages {
    /** Off-screen holder — remove it when done. */
    container: HTMLElement;
    pages: HTMLElement[];
}

const esc = (value: unknown): string =>
    String(value ?? "").replace(
        /[&<>"']/g,
        (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c] as string,
    );

const BANGLA = /[ঀ-৿]/;
const taka = (n: number) => `৳${formatMoney(n)}`;

const DATE = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "long", year: "numeric" });
const STAMP = new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
});
const formatDate = (value?: string) => (value ? DATE.format(new Date(value)) : "");

const STATUS: Record<string, string> = {
    PENDING: "Pending",
    CONFIRMED: "Confirmed",
    RECEIVED: "Received",
    PARTIAL_RECEIVED: "Partially received",
    CANCELLED: "Cancelled",
    ON_HOLD: "On hold",
};

/** Column widths in the table grid. */
const COLUMN_WIDTH: Record<string, string> = {
    sl: "34px",
    image: "40px",
    product: "minmax(0, 1fr)",
    sku: "104px",
    qty_ordered: "50px",
    qty_received: "70px",
    unit_cost: "80px",
    total_cost: "88px",
};
const MONEY_COLUMNS = new Set(["unit_cost", "total_cost"]);

const STYLE = `
.po-pdf, .po-pdf * { box-sizing: border-box; margin: 0; }
.po-pdf { position: absolute; left: -10000px; top: 0; width: ${PAGE_W}px; font-family: ${F.text};
  color: ${T.ink}; -webkit-font-smoothing: antialiased; font-size: 13px; line-height: 1.35; }
.po-page { position: relative; width: ${PAGE_W}px; height: ${PAGE_H}px; overflow: hidden; background: ${T.page}; }
.po-measure .po-page { height: auto; }
.po-band { background: ${T.teal}; height: 132px; padding: 0 40px; display: flex; align-items: center; justify-content: space-between; gap: 24px; }
.po-brand { display: flex; align-items: center; gap: 22px; min-width: 0; }
.po-logo { width: 78px; height: 78px; border-radius: 14px; background: ${T.cream}; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.po-logo img { width: 58px; height: 58px; object-fit: contain; }
.po-shop { font-family: ${F.heading}; font-weight: 600; font-size: 31px; line-height: 1.1; color: #fbf7ef; }
.po-site { font-weight: 600; font-size: 13px; color: ${T.goldText}; letter-spacing: 0.04em; margin-top: 7px; }
.po-to { text-align: right; min-width: 0; }
.po-eyebrow { font-weight: 700; font-size: 11px; letter-spacing: 0.2em; color: ${T.goldText}; text-transform: uppercase; }
.po-to-name { font-family: ${F.heading}; font-weight: 500; font-size: 26px; line-height: 1.15; color: #fbf7ef; margin-top: 6px; }
.po-rule { height: 4px; background: ${T.gold}; }
.po-strip { background: ${T.teal}; height: 44px; padding: 0 40px; display: flex; align-items: center; justify-content: space-between; color: #dbe6e4; font-size: 12px; }
.po-strip b { font-family: ${F.heading}; font-weight: 600; font-size: 15px; color: #fbf7ef; }
.po-body { padding: ${BODY_TOP}px 40px 0; }
.po-meta { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); column-gap: 22px; row-gap: 16px; padding-bottom: 22px; }
.po-label { font-size: 10.5px; font-weight: 600; letter-spacing: 0.14em; text-transform: uppercase; color: ${T.muted}; }
.po-value { font-size: 13.5px; font-weight: 600; color: ${T.ink}; margin-top: 5px; overflow-wrap: anywhere; }
.po-table { border: 1px solid ${T.border}; border-radius: 10px; overflow: hidden; background: ${T.row}; }
.po-tr { display: grid; column-gap: 12px; padding-left: 18px; padding-right: 18px; align-items: start; }
.po-th { background: ${T.tableHead}; padding-top: 13px; padding-bottom: 12px; font-size: 10.5px; font-weight: 700;
  letter-spacing: 0.1em; text-transform: uppercase; color: ${T.teal}; border-bottom: 1px solid ${T.border}; }
.po-row { padding-top: 9px; padding-bottom: 9px; border-bottom: 1px solid ${T.rowLine}; font-size: 13px; }
.po-row.po-last { border-bottom: 0; }
.po-bandrow { background: ${T.band}; padding: 8px 18px; display: flex; align-items: center; gap: 10px;
  border-bottom: 1px solid ${T.rowLine}; }
.po-band-name { font-size: 11px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; color: ${T.teal}; }
.po-band-sum { font-size: 10.5px; font-weight: 600; color: ${T.muted}; background: ${T.row}; border: 1px solid ${T.border};
  border-radius: 999px; padding: 2px 9px; white-space: nowrap; }
.po-right { text-align: right; } .po-center { text-align: center; }
.po-no { color: ${T.muted}; font-weight: 500; }
.po-thumb { width: 40px; height: 40px; border-radius: 8px; border: 1px solid ${T.border}; background: ${T.band}; overflow: hidden; }
.po-thumb img { display: block; width: 38px; height: 38px; }
.po-name { font-weight: 600; font-size: 13.5px; line-height: 1.3; overflow-wrap: anywhere; }
.po-sub { color: ${T.muted}; font-size: 11.5px; margin-top: 2px; overflow-wrap: anywhere; }
.po-num { font-variant-numeric: lining-nums; }
.po-bold { font-weight: 700; } .po-semi { font-weight: 600; }
.po-free { color: ${T.gold}; font-weight: 600; }
.po-tail { padding-top: 26px; }
.po-summary { display: grid; grid-template-columns: minmax(0, 1fr) 330px; column-gap: 40px; align-items: start; }
.po-qty { font-size: 15px; margin-top: 7px; color: ${T.ink}; } .po-qty b { font-weight: 700; }
.po-sumrow { display: flex; justify-content: space-between; gap: 14px; font-size: 12.5px; color: ${T.muted}; padding: 8px 0;
  border-bottom: 1px solid ${T.footerRule}; }
.po-sumrow span + span { color: ${T.ink}; font-weight: 600; white-space: nowrap; }
.po-total { margin-top: 10px; background: ${T.teal}; border-radius: 10px; padding: 16px 20px; display: flex;
  justify-content: space-between; align-items: center; gap: 12px; color: #fbf7ef; }
.po-total-label { font-size: 13px; font-weight: 600; }
.po-total-value { font-family: ${F.heading}; font-weight: 600; font-size: 27px; white-space: nowrap; }
.po-received { display: flex; justify-content: space-between; font-size: 12.5px; padding: 9px 4px 0; color: ${T.ink}; }
.po-received span + span { font-weight: 700; }
.po-notes-text { font-size: 12.5px; line-height: 1.55; white-space: pre-wrap; margin-top: 6px; color: ${T.ink}; overflow-wrap: anywhere; }
.po-footer { position: absolute; left: 40px; right: 40px; bottom: ${FOOTER_BOTTOM}px; border-top: 1px solid ${T.footerRule};
  padding-top: 14px; display: flex; justify-content: space-between; align-items: flex-end; gap: 24px; }
.po-measure .po-footer { position: static; }
.po-foot-left { font-size: 11.5px; color: ${T.muted}; line-height: 1.6; min-width: 0; }
.po-foot-bn { font-family: ${F.bangla}; }
.po-foot-name { font-size: 12.5px; font-weight: 600; color: ${T.ink}; }
.po-foot-gen { font-size: 10px; color: #8a9593; margin-top: 4px; }
.po-foot-right { text-align: right; flex-shrink: 0; }
.po-foot-site { font-family: ${F.heading}; font-weight: 600; font-size: 19px; color: ${T.teal}; margin-top: 3px; }
.po-page-no { font-size: 10.5px; color: ${T.muted}; margin-top: 4px; letter-spacing: 0.04em; }
`;

/** The second line under a product: category, gift, bonus, note. */
function subLine(item: PurchaseItem): string {
    const parts = [
        item.product?.category?.name,
        item.is_gift ? "Gift" : null,
        Number(item.bonus_quantity) > 0 ? `+${item.bonus_quantity} bonus` : null,
        item.notes?.trim() || null,
    ].filter(Boolean);
    return parts.join(" · ");
}

function cell(col: ColumnConfig, line: PoLine, thumbnails?: Map<string, string>): string {
    const { item } = line;
    const align = col.align === "right" ? "po-right" : col.align === "center" ? "po-center" : "";
    const wrap = (inner: string, extra = "") => `<div class="${align} ${extra}">${inner}</div>`;
    switch (col.key) {
        case "sl":
            return wrap(String(line.no), "po-no po-num");
        case "image": {
            // An empty tile when the product has no photo, so names still line up.
            const src = item.product ? thumbnails?.get(item.product.product_id) : undefined;
            return `<div class="po-thumb">${src ? `<img src="${esc(src)}" alt="">` : ""}</div>`;
        }
        case "product": {
            const sub = subLine(item);
            return `<div class="${align}"><div class="po-name">${esc(item.product?.name || "N/A")}</div>${
                sub ? `<div class="po-sub">${esc(sub)}</div>` : ""
            }</div>`;
        }
        case "sku":
            return wrap(esc(item.product?.sku || "—"), "po-num");
        case "qty_ordered":
            return wrap(String(item.quantity_ordered), "po-num po-semi");
        case "qty_received":
            return wrap(String(item.quantity_received), "po-num");
        case "unit_cost":
            return item.is_gift ? wrap("Free", "po-free") : wrap(formatMoney(Number(item.unit_cost)), "po-num");
        case "total_cost":
            return item.is_gift ? wrap("Free", "po-free") : wrap(formatMoney(line.lineTotal), "po-num po-bold");
        default:
            return wrap("");
    }
}

/** English and Bangla address lines, drawn in their own fonts. */
function addressParts(info: CompanyInfo): { english: string; bangla: string } {
    const lines = info.addressLines?.length ? info.addressLines : info.address ? [info.address] : [];
    return {
        english: lines.filter((l) => !BANGLA.test(l)).join(", "),
        bangla: lines.filter((l) => BANGLA.test(l)).join(", "),
    };
}

function headerFieldValue(field: HeaderFieldConfig, purchase: Purchase): string {
    switch (field.key) {
        case "po_number":
            return purchase.purchase_number;
        case "date":
            return formatDate(purchase.purchase_date);
        case "supplier":
            return purchase.supplier?.name ?? "";
        case "status":
            return STATUS[purchase.status] ?? purchase.status;
        case "expected_delivery":
            return formatDate(purchase.expected_delivery_date);
        default:
            return "";
    }
}

export function buildPurchaseOrderPages(purchase: Purchase, options: PoPageOptions): BuiltPages {
    const { columns, headerFields, includeShipping, showSummary, companyInfo: info, logoDataUrl } = options;
    const enabledColumns = columns.filter((c) => c.enabled);
    const gridTemplate = enabledColumns.map((c) => COLUMN_WIDTH[c.key] ?? "auto").join(" ");
    const groups: BrandGroup[] = groupByBrand(purchase.items);
    const showBands = !(groups.length === 1 && groups[0].brand === null);
    const supplierShown = headerFields.some((f) => f.key === "supplier" && f.enabled) && !!purchase.supplier?.name;
    const address = addressParts(info);

    // ── Blocks ──────────────────────────────────────────────────────────
    const band = `
      <div class="po-band">
        <div class="po-brand">
          ${logoDataUrl ? `<div class="po-logo"><img src="${esc(logoDataUrl)}" alt=""></div>` : ""}
          <div><div class="po-shop">${esc(info.name)}</div>${info.website ? `<div class="po-site">${esc(info.website)}</div>` : ""}</div>
        </div>
        <div class="po-to">
          <div class="po-eyebrow">Purchase order</div>
          <div class="po-to-name">${supplierShown ? `To ${esc(purchase.supplier!.name)}` : esc(formatDate(purchase.purchase_date))}</div>
        </div>
      </div><div class="po-rule"></div>`;
    const strip = `
      <div class="po-strip"><b>${esc(info.name)}</b><span>Purchase order ${esc(purchase.purchase_number)} · continued</span></div>
      <div class="po-rule"></div>`;

    const metaItems: { label: string; value: string }[] = [
        { label: "Ordered by", value: info.name },
        ...headerFields
            .filter((f) => f.enabled)
            .map((f) => ({ label: f.label, value: headerFieldValue(f, purchase) }))
            .filter((m) => m.value),
        ...(purchase.reference_number ? [{ label: "Reference", value: purchase.reference_number }] : []),
    ];
    const meta = `<div class="po-meta">${metaItems
        .map((m) => `<div><div class="po-label">${esc(m.label)}</div><div class="po-value">${esc(m.value)}</div></div>`)
        .join("")}</div>`;

    const head = `<div class="po-tr po-th" style="grid-template-columns:${gridTemplate}">${enabledColumns
        .map((c) => {
            const align = c.align === "right" ? "po-right" : c.align === "center" ? "po-center" : "";
            // The photo column needs no heading.
            const label = c.key === "sl" ? "No." : c.key === "image" ? "" : c.label;
            return `<div class="${align}">${esc(label)}${MONEY_COLUMNS.has(c.key) ? " ৳" : ""}</div>`;
        })
        .join("")}</div>`;

    // Table blocks: brand bands and rows, in print order.
    const tableBlocks: { html: string; keepWithNext: boolean; isRow: boolean }[] = [];
    groups.forEach((group) => {
        if (showBands) {
            tableBlocks.push({
                // The brand's subtotal sits next to its name, away from the
                // Total column, so adding up that column never picks it up.
                html: `<div class="po-bandrow"><span class="po-band-name">${esc(group.brand ?? "Others")}</span><span class="po-band-sum">${
                    group.lines.length
                } item${group.lines.length === 1 ? "" : "s"} · ${esc(taka(group.subtotal))}</span></div>`,
                keepWithNext: true,
                isRow: false,
            });
        }
        group.lines.forEach((line) =>
            tableBlocks.push({
                html: `<div class="po-tr po-row" style="grid-template-columns:${gridTemplate}">${enabledColumns
                    .map((c) => cell(c, line, options.thumbnails))
                    .join("")}</div>`,
                keepWithNext: false,
                isRow: true,
            }),
        );
    });

    // Tail blocks: summary, notes.
    const tailBlocks: string[] = [];
    if (showSummary) {
        const totalQty = purchase.items.reduce((s, i) => s + Number(i.quantity_ordered || 0), 0);
        const receivedQty = purchase.items.reduce((s, i) => s + Number(i.quantity_received || 0), 0);
        // total_amount is the plain item subtotal; discount, tax and shipping
        // apply on top (the API's Purchase.net_payable, the drawer's math).
        const orderedSubtotal = Number(purchase.total_amount);
        const receivedSubtotal = purchase.items.reduce(
            (sum, i) => sum + Number(i.quantity_received) * Number(i.unit_cost),
            0,
        );
        const partial = receivedSubtotal < orderedSubtotal && receivedSubtotal > 0;
        const discount = Number(purchase.discount_amount);
        const tax = Number(purchase.tax_amount);
        const shipping = Number(purchase.shipping_cost);
        const orderedTotal = orderedSubtotal - discount + tax + (includeShipping ? shipping : 0);
        const receivedTotal = receivedSubtotal + tax - discount + (includeShipping ? shipping : 0);

        const rows: [string, string][] = [];
        if (partial || discount > 0 || tax > 0 || includeShipping) rows.push(["Subtotal (ordered)", taka(orderedSubtotal)]);
        if (partial) rows.push(["Subtotal (received)", taka(receivedSubtotal)]);
        if (discount > 0) rows.push(["Discount", `−${taka(discount)}`]);
        if (tax > 0) rows.push(["Tax", taka(tax)]);
        if (includeShipping) rows.push(["Shipping", taka(shipping)]);

        tailBlocks.push(`
          <div class="po-tail"><div class="po-summary">
            <div>
              <div class="po-label">Quantity</div>
              <div class="po-qty"><b>${totalQty} pcs</b> across ${purchase.items.length} line${purchase.items.length === 1 ? "" : "s"}${
                  receivedQty > 0 ? ` · ${receivedQty} received` : ""
              }</div>
            </div>
            <div>
              ${rows.map(([l, v]) => `<div class="po-sumrow"><span>${esc(l)}</span><span>${esc(v)}</span></div>`).join("")}
              <div class="po-total"><span class="po-total-label">Order total</span><span class="po-total-value">${esc(taka(orderedTotal))}</span></div>
              ${partial ? `<div class="po-received"><span>Received total</span><span>${esc(taka(receivedTotal))}</span></div>` : ""}
            </div>
          </div></div>`);
    }
    if (purchase.notes?.trim()) {
        tailBlocks.push(
            `<div class="po-tail"><div class="po-label">Notes</div><div class="po-notes-text">${esc(purchase.notes.trim())}</div></div>`,
        );
    }

    const contacts = [
        info.phone,
        info.whatsapp ? `WhatsApp ${info.whatsapp}` : null,
        info.email,
    ].filter(Boolean);
    const footer = (pageNo: number, pageCount: number) => `
      <div class="po-footer">
        <div class="po-foot-left">
          <div class="po-foot-name">${esc(info.name)}</div>
          ${address.english ? `<div>${esc(address.english)}</div>` : ""}
          ${address.bangla ? `<div class="po-foot-bn">${esc(address.bangla)}</div>` : ""}
          ${contacts.length ? `<div>${esc(contacts.join(" · "))}</div>` : ""}
        </div>
        <div class="po-foot-right">
          ${info.website ? `<div class="po-label">Website</div><div class="po-foot-site">${esc(info.website)}</div>` : ""}
          ${pageCount > 1 ? `<div class="po-page-no">Page ${pageNo} of ${pageCount}</div>` : ""}
          <div class="po-foot-gen">Generated ${esc(STAMP.format(options.generatedAt))}</div>
        </div>
      </div>`;

    // ── Measure ─────────────────────────────────────────────────────────
    const container = document.createElement("div");
    container.className = "po-pdf";
    container.innerHTML = `<style>${STYLE}</style>`;
    document.body.appendChild(container);

    const measure = document.createElement("div");
    measure.className = "po-measure";
    measure.innerHTML = `
      <div class="po-page">
        <div data-m="band">${band}</div>
        <div data-m="strip">${strip}</div>
        <div class="po-body">
          <div data-m="meta">${meta}</div>
          <div class="po-table"><div data-m="head">${head}</div>${tableBlocks
              .map((b, i) => `<div data-m="t${i}">${b.html}</div>`)
              .join("")}</div>
          ${tailBlocks.map((html, i) => `<div data-m="x${i}">${html}</div>`).join("")}
        </div>
        <div data-m="footer">${footer(2, 2)}</div>
      </div>`;
    container.appendChild(measure);
    const h = (key: string) => (measure.querySelector(`[data-m="${key}"]`) as HTMLElement).getBoundingClientRect().height;

    const footerReserve = h("footer") + FOOTER_BOTTOM + FOOTER_GAP;
    const tableChrome = h("head") + 2; // head row + the table's top/bottom border
    const firstCapacity = PAGE_H - h("band") - BODY_TOP - h("meta") - tableChrome - footerReserve;
    const nextCapacity = PAGE_H - h("strip") - BODY_TOP - tableChrome - footerReserve;
    const blocks: PackBlock[] = [
        ...tableBlocks.map((b, i) => ({ height: h(`t${i}`), keepWithNext: b.keepWithNext })),
        ...tailBlocks.map((_, i) => ({ height: h(`x${i}`) })),
    ];
    const packed = packPages(blocks, firstCapacity, nextCapacity);
    measure.remove();

    // ── Pages ───────────────────────────────────────────────────────────
    const pages = packed.map((indexes, p) => {
        const tableIdx = indexes.filter((i) => i < tableBlocks.length);
        const tailIdx = indexes.filter((i) => i >= tableBlocks.length).map((i) => i - tableBlocks.length);
        const rowsHtml = tableIdx
            .map((i, n) =>
                n === tableIdx.length - 1 && tableBlocks[i].isRow
                    ? tableBlocks[i].html.replace('class="po-tr po-row"', 'class="po-tr po-row po-last"')
                    : tableBlocks[i].html,
            )
            .join("");
        const page = document.createElement("div");
        page.className = "po-page";
        page.innerHTML = `
          ${p === 0 ? band : strip}
          <div class="po-body">
            ${p === 0 ? meta : ""}
            ${tableIdx.length ? `<div class="po-table">${head}${rowsHtml}</div>` : ""}
            ${tailIdx.map((i) => tailBlocks[i]).join("")}
          </div>
          ${footer(p + 1, packed.length)}`;
        container.appendChild(page);
        return page;
    });

    return { container, pages };
}

/** Waits for the pages' images (the logo) so they're drawn, not blank. */
export async function imagesReady(container: HTMLElement): Promise<void> {
    const images = Array.from(container.querySelectorAll("img"));
    await Promise.all(images.map((img) => (img.complete ? Promise.resolve() : img.decode().catch(() => undefined))));
}

